import React, { useState, useEffect, useRef } from "react";
import { Input, Popover } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import type { PasswordProps } from "antd/es/input";
import "./PasswordFieldWithValidation.scss";

interface ValidationRule {
  label: string;
  test: (password: string) => boolean;
}

interface PasswordFieldWithValidationProps
  extends Omit<PasswordProps, "onChange"> {
  onValidationChange?: (isValid: boolean) => void;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const PasswordFieldWithValidation: React.FC<
  PasswordFieldWithValidationProps
> = ({
  onValidationChange,
  value = "",
  onChange,
  className = "",
  ...restProps
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<any>(null);

  // Define validation rules in the order shown in the screenshot
  const validationRules: ValidationRule[] = [
    {
      label: "At least 8 characters",
      test: (pwd) => pwd.length >= 8,
    },
    {
      label: "At least one number",
      test: (pwd) => /\d/.test(pwd),
    },
    {
      label: "At least one special character",
      test: (pwd) => /[!@#$%^&*()+\-=\[\]{}|;':",.<>?/\\]/.test(pwd),
    },
    {
      label: "At least one uppercase letter",
      test: (pwd) => /[A-Z]/.test(pwd),
    },
    {
      label: "At least one lowercase letter",
      test: (pwd) => /[a-z]/.test(pwd),
    },
  ];

  // Check if password is valid (all rules passed)
  const isValid = React.useMemo(() => {
    const password = value || "";
    if (!password) return false;
    return validationRules.every((rule) => rule.test(password));
  }, [value]);

  // Update parent component when validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // Generate validation popup content
  const validationContent = (
    <div className="password-validation-popup">
      <div className="validation-popup-header">
        Password requirements must meet:
      </div>
      <div className="validation-popup-rules">
        {validationRules.map((rule, index) => {
          const isMet = rule.test(value || "");
          return (
            <div
              key={index}
              className={`validation-rule-item ${isMet ? "met" : "unmet"}`}
            >
              <span className="rule-icon">
                {isMet ? (
                  <CheckCircleFilled className="check-icon" />
                ) : (
                  <CloseCircleFilled className="cross-icon" />
                )}
              </span>
              <span className="rule-text">{rule.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Determine if popover should be visible
  const showPopover = (isHovered || isFocused) && !isValid && value.length > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (restProps.onFocus) {
      restProps.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (restProps.onBlur) {
      restProps.onBlur(e);
    }
  };

  return (
    <Popover
      content={validationContent}
      visible={showPopover}
      onVisibleChange={() => {}} // Controlled by hover/focus state, prevent default behavior
      placement="left"
      overlayClassName="password-validation-popover"
      getPopupContainer={(triggerNode) =>
        triggerNode.parentElement || document.body
      }
    >
      <div
        className="password-field-wrapper"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Input.Password
          {...restProps}
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={className}
        />
      </div>
    </Popover>
  );
};

export default PasswordFieldWithValidation;
